export const DESERT_TOUR_STORAGE_KEY = 'cityExplorer:desertMapTour:v1';

export const isDesertCategoryId = (id) => String(id || '').toLowerCase().includes('desert');

export const locationHasDesertMaps = (categories = []) =>
  categories.some((category) => isDesertCategoryId(category?.id));

export const isDesertMapItem = (item) =>
  item?.type === 'map' && isDesertCategoryId(item.category?.id);

const TRAVEL_PATTERN = /(\d+)\s*-?\s*minute\s+(drive|walk)/i;

export const desertTravelLabel = (category) => {
  const text = `${category?.description || ''} ${category?.label || ''}`;
  const match = text.match(TRAVEL_PATTERN);
  if (match) {
    const mode = match[2].toLowerCase() === 'walk' ? 'walk' : 'drive';
    return `${match[1]}-minute ${mode}`;
  }
  return '10-minute drive';
};

export const desertCoverageFilter = (category) => {
  const id = String(category?.id || '').toLowerCase();
  const label = String(category?.label || '').toLowerCase();
  const combined = id.includes('combined') || id.includes('all_schools');
  const quality = id.includes('quality') || label.includes('quality');
  const district = id.includes('district') && !combined;

  if (combined) {
    return {
      school: 'high-quality school of any type',
      dynamic:
        'Coverage only counts schools that clear the quality bar — nearby schools below that bar do not fill the gap.',
    };
  }
  if (quality && district) {
    return {
      school: 'high-quality district school',
      dynamic:
        'A nearby district school does not count as coverage unless it meets the quality bar on this map.',
    };
  }
  if (quality) {
    return {
      school: 'high-quality charter school',
      dynamic:
        'Quality is the filter here: a charter school that is nearby but below the bar does not count as coverage.',
    };
  }
  if (district) {
    return { school: 'district school', dynamic: null };
  }
  return { school: 'charter school', dynamic: null };
};

export const desertTourSteps = (category) => {
  const travel = desertTravelLabel(category);
  const { school, dynamic } = desertCoverageFilter(category);
  const travelKind = travel.includes('walk') ? 'walking time' : 'drive time';

  return [
    {
      id: 'drive-times',
      title: `Based on a ${travel}`,
      body: `Shaded areas are neighborhoods a ${school} cannot reach within a ${travel}. That uses real ${travelKind} — not a straight-line radius. The legend below the map shows how those gaps are colored.`,
      spotlight: 'legend-box',
    },
    {
      id: 'desert-example',
      title: 'Where there is a desert',
      body: `Solid colored patches are deserts: no ${school} is within a ${travel} of that area.${
        dynamic ? ` ${dynamic}` : ''
      } The highlighted area is one of those gaps — not the unshaded city in the middle.`,
      spotlight: 'map-desert',
      spotlightLabel: 'Desert',
    },
    {
      id: 'covered-example',
      title: 'Where there is not a desert',
      body: `Unshaded areas already have coverage. At least one ${school} is within a ${travel}, so those neighborhoods are not a desert on this map.`,
      spotlight: 'map-covered',
      spotlightLabel: 'Covered',
    },
    {
      id: 'explore',
      title: 'Explore the map',
      body: 'Hover a shaded area or school marker for details. Use the + and − buttons in the top left to zoom, or scroll or pinch.',
      spotlight: 'map-zoom',
      spotlightLabel: 'Zoom',
    },
    {
      id: 'school-popups',
      title: 'School details',
      body: 'Click a school marker to open a pop-up with name, enrollment, and grades. The highlighted card is an example of what you will see.',
      spotlight: 'example-popup',
    },
  ];
};

export const readDesertTourDismissed = () => {
  try {
    return window.localStorage.getItem(DESERT_TOUR_STORAGE_KEY) === '1';
  } catch (_err) {
    return false;
  }
};

export const writeDesertTourDismissed = () => {
  try {
    window.localStorage.setItem(DESERT_TOUR_STORAGE_KEY, '1');
  } catch (_err) {
    // Private browsing or disabled storage should not break the map.
  }
};

export const shouldShowDesertTour = ({
  hasDesertCategory,
  viewingDesertMap,
  mapLoaded,
  isFullscreen,
  dismissed,
  replaying,
}) =>
  Boolean(
    hasDesertCategory &&
      viewingDesertMap &&
      mapLoaded &&
      !isFullscreen &&
      (!dismissed || replaying)
  );
